import React, { useCallback, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import Text from '~/components/core/TextComponents/Text';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import AttachmentIcon from '~/components/icons/AttachmentIcon';
import TrashIcon from '~/components/icons/TrashIcon';
import InlineIconButton from '~/components/InlineIconButton';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import TextFieldFormik from '~/components/TextFieldFormik';
import cn from '~/Utils/cn';

import { useMetabaseQuestions } from './hooks/useMetabaseQuestions';
import type { TemplateUploadResponse } from './hooks/useReportTemplates';
import { tokenMatchesColumn } from './columnMatching';
import ReportSheetConfigCard from './ReportSheetConfigCard';
import { getSheetsDetectedText, REPORT_TEMPLATES_TEXTS } from './texts';
import type { FormValues, SheetsValidationState } from './types';

const TOAST_ID = 'report-template-form-errors';

const ValidationToast: React.FC = () => {
  useFormikValidationErrorToast({
    toastId: TOAST_ID,
    title: REPORT_TEMPLATES_TEXTS.VALIDATION_TOAST_TITLE,
  });
  return null;
};

const validationSchema = Yup.object().shape({
  templateName: Yup.string().required(REPORT_TEMPLATES_TEXTS.TEMPLATE_NAME_REQUIRED),
  sheets: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number().optional(),
        sheet_name: Yup.string().required(),
        display_name: Yup.string().required(REPORT_TEMPLATES_TEXTS.DISPLAY_NAME_REQUIRED),
        metabase_question_id: Yup.number().nullable(),
      })
    )
    .test('at-least-one-configured', REPORT_TEMPLATES_TEXTS.AT_LEAST_ONE_SHEET_REQUIRED, (sheets) => {
      if (!sheets) return false;
      return sheets.some((sheet) => sheet.metabase_question_id !== null && (sheet.display_name || '').trim());
    }),
});

export interface ReportTemplateFormProps {
  /** Mode affects button text */
  mode: 'create' | 'edit';
  /** Initial form values */
  initialValues: FormValues;
  /** Uploaded file data (for displaying filename and sheet count) */
  uploadedData: TemplateUploadResponse;
  /** Initial validation state for sheets */
  initialSheetsValidationState: SheetsValidationState;
  /** Called when form is submitted with validated values */
  onSubmit: (values: FormValues) => Promise<void>;
  /** Called when cancel button is clicked */
  onCancel: () => void;
  /** Called when delete file icon is clicked to clear the file and show upload step */
  onDeleteFile: () => void;
  /** Whether parent is in submitting state */
  isSubmitting: boolean;
}

const ReportTemplateForm: React.FC<ReportTemplateFormProps> = ({
  mode,
  initialValues,
  uploadedData,
  initialSheetsValidationState,
  onSubmit,
  onCancel,
  onDeleteFile,
  isSubmitting,
}) => {
  const { questions, isLoadingQuestions, getQuestionColumns } = useMetabaseQuestions();

  // Local validation state that can be updated as user selects questions
  const [sheetsValidationState, setSheetsValidationState] =
    useState<SheetsValidationState>(initialSheetsValidationState);

  // Sync validation state when initialSheetsValidationState prop changes (e.g., after file replacement in edit mode)
  useEffect(() => {
    setSheetsValidationState(initialSheetsValidationState);
  }, [initialSheetsValidationState]);

  // Uses functional state updates to avoid sheetsValidationState in dependencies (prevents infinite loops)
  const handleQuestionValidation = useCallback(
    async (sheetName: string, questionId: number) => {
      // Capture extracted_variables before async operation using functional update
      let extractedVariables: string[] = [];
      setSheetsValidationState((prev) => {
        extractedVariables = prev[sheetName]?.extracted_variables || [];
        return {
          ...prev,
          [sheetName]: { ...prev[sheetName], isValidating: true },
        };
      });

      const columns = await getQuestionColumns(questionId);
      if (columns) {
        // Match against display_name (what users see in Metabase), folding case and the
        // arrow / -> / underscore / whitespace separators so a copied display-name token and its
        // hand-typed ASCII equivalent both match — mirrors the backend column_name_matching.
        // De-duplicated because a self-join question can expose two identically-named columns,
        // which the backend collapses to one owner and which would otherwise collide as React
        // keys on the copy-token chips.
        const columnDisplayNames = Array.from(new Set((columns.columns ?? []).map((col) => col.display_name)));

        // One pass, so matched/unmatched stay complementary and each token resolves once.
        const matched: string[] = [];
        const unmatched: string[] = [];
        extractedVariables.forEach((v) => (tokenMatchesColumn(v, columnDisplayNames) ? matched : unmatched).push(v));

        setSheetsValidationState((prev) => ({
          ...prev,
          [sheetName]: {
            ...prev[sheetName],
            matchedVariables: matched,
            unmatchedVariables: unmatched,
            availableColumns: columnDisplayNames,
            isValidating: false,
          },
        }));
      } else {
        setSheetsValidationState((prev) => ({
          ...prev,
          [sheetName]: {
            ...prev[sheetName],
            // The fetch failed, so the previous question's columns are no longer the ones on
            // offer. Clearing them beats showing tokens that are guaranteed not to resolve.
            availableColumns: undefined,
            isValidating: false,
          },
        }));
      }
    },
    [getQuestionColumns]
  );

  const submitButtonText =
    mode === 'create' ? REPORT_TEMPLATES_TEXTS.CREATE_TEMPLATE : REPORT_TEMPLATES_TEXTS.SAVE_CHANGES;

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
      {({ handleSubmit: formikHandleSubmit, isSubmitting: formikIsSubmitting, values }) => (
        <div className="flex flex-col gap-16">
          <ValidationToast />
          <ValidationErrorToastContainer containerId={TOAST_ID} />
          <div>
            <TextFieldFormik
              id="templateName"
              label={REPORT_TEMPLATES_TEXTS.TEMPLATE_NAME_LABEL}
              fullWidth
              disabled={formikIsSubmitting || isSubmitting}
            />
          </div>

          <div
            className={cn('flex w-full items-center justify-between rounded bg-slate-200 p-8', {
              'bg-opacity-50': formikIsSubmitting || isSubmitting,
            })}
          >
            <div
              className={cn('flex items-center gap-8', {
                'opacity-50': formikIsSubmitting || isSubmitting,
              })}
            >
              <AttachmentIcon />
              <OverflowTextWithToolTip maxWidth="200px">
                {uploadedData?.filename ?? REPORT_TEMPLATES_TEXTS.UNKNOWN}
              </OverflowTextWithToolTip>
              <Text variant={Text.VARIANTS.XS} className="text-slate-500">
                {getSheetsDetectedText(values.sheets?.length ?? 0)}
              </Text>
            </div>
            <InlineIconButton
              icon={TrashIcon}
              tooltipTitle={REPORT_TEMPLATES_TEXTS.REMOVE_FILE}
              iconColor="currentColor"
              className="cursor-pointer hover:text-teal-700"
              onClick={onDeleteFile}
              defaultColor={undefined}
              disabled={formikIsSubmitting || isSubmitting}
              iconStyle={undefined}
              useIconButton={undefined}
              ignorePermissions={undefined}
              ariaLabel={undefined}
            />
          </div>

          <div>
            <Text variant={Text.VARIANTS.SM} className="mb-12">
              {REPORT_TEMPLATES_TEXTS.SELECT_TABLE_INSTRUCTION}
            </Text>

            {(values.sheets ?? []).map((sheet, index) => (
              <ReportSheetConfigCard
                key={sheet.sheet_name}
                sheetName={sheet.sheet_name}
                index={index}
                questions={questions}
                isLoadingQuestions={isLoadingQuestions}
                isSubmitting={formikIsSubmitting || isSubmitting}
                validationState={sheetsValidationState[sheet.sheet_name]}
                onQuestionValidation={handleQuestionValidation}
              />
            ))}
          </div>

          <div className="mt-16 flex w-full justify-end">
            <CancelButton disabled={formikIsSubmitting || isSubmitting} onClick={onCancel} />
            <Button
              variant="contained"
              color="primary"
              disabled={formikIsSubmitting || isSubmitting}
              onClick={() => formikHandleSubmit()}
            >
              {submitButtonText}
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default ReportTemplateForm;
